import { COMMON_JOB_STATUS, RIGHT_SIDE_BAR_TYPES, UPLOAD_FILE_LOCATION_TYPE, UPLOAD_LOCATION } from "@constants";
import { changeActiveTab, changeIsAdminSetting, toggleActive } from "@modules/rightSideBar";
import { useDispatch, useSelector } from "react-redux";
import { deleteMyJobsJob, updateMyJobsJob, updateMyJobsTask, updateRunningJobIds } from "@modules/myJobs";
import { useEffect, useMemo, useState } from "react";
import { nanoid } from "nanoid";
import * as jobStatus from "@cores/jobStatus";

const useMyJobs = ({ resource = null, store = "uploadQueue" }) => {
    const dispatch = useDispatch();

    const myJobs = useSelector(({ myJobs }) => myJobs);
    const upload = useSelector(({ upload }) => upload);
    const uploadQueue = useSelector(({ uploadQueue }) => uploadQueue);
    const downloadJobs = useSelector(({ downloadJobs }) => downloadJobs);
    const bulkJobs = useSelector(({ bulkJobs }) => bulkJobs);

    const fileData = useSelector((state) => state[store]);
    const [runningJobIds, setRunningJobIds] = useState([]);
    const [isSynchronization, setIsSynchronization] = useState(false);

    //change active tab 기능 옮기기
    const changeMyJobsActiveTab = (activeTab) => {
        dispatch(changeActiveTab({ activeTab }));
    };

    const openMyJobs = (activeTab) => {
        dispatch(
            toggleActive({
                type: RIGHT_SIDE_BAR_TYPES.MY_JOB,
                activeTab,
                activeFlag: true,
            }),
        );
    };

    // const updateIsAdminSetting = (value) => {
    //     dispatch(changeIsAdminSetting({ isAdminSetting: value }));
    // };

    const isJobExisting = useMemo(() => {
        return (
            upload.jobs.data.length > 0 ||
            uploadQueue[UPLOAD_FILE_LOCATION_TYPE.LOCAL].length > 0 ||
            uploadQueue[UPLOAD_LOCATION.CREATE_ASSET].length > 0 ||
            myJobs.data.length > 0 ||
            downloadJobs.data.length > 0 ||
            bulkJobs.data.length > 0
        );
    }, [upload, uploadQueue, myJobs, downloadJobs, bulkJobs]);

    const isJobProcessing = useMemo(() => {
        return (
            upload.jobs.data.filter((v) => !jobStatus.isComplete(v.status)).length > 0 ||
            uploadQueue[UPLOAD_FILE_LOCATION_TYPE.LOCAL].filter((v) => !jobStatus.isComplete(v.status)).length > 0 ||
            uploadQueue[UPLOAD_LOCATION.CREATE_ASSET].filter((v) => !jobStatus.isComplete(v.status)).length > 0 ||
            myJobs.data.filter((v) => {
                return !(v.status === "CANCELED" || v.status === "FAILED" || v.status === "SUCCEED");
            }).length > 0 ||
            downloadJobs.data.filter((v) => {
                return !(v.status === "CANCELED" || v.status === "FAILED" || v.status === "SUCCEED");
            }).length > 0 ||
            bulkJobs.data.filter((v) => {
                return !(v.status === "CANCELED" || v.status === "FAILED" || v.status === "SUCCEED");
            }).length > 0
        );
    }, [upload, uploadQueue, myJobs, downloadJobs, bulkJobs]);

    //Note: 브라우저가 «직접 전송 중»인가. 서버에서 도는 잡(트랜스코드 등)과 달리 페이지를 닫으면 그 자리에서 끊긴다.
    //isJobProcessing 은 서버 잡까지 포함하므로 이 구분에는 못 쓴다. App.js 의 이탈 경고도 같은 두 큐를 본다.
    const isBrowserUploading = useMemo(
        () =>
            [
                ...(uploadQueue?.[UPLOAD_LOCATION.CREATE_ASSET] ?? []),
                ...(uploadQueue?.[UPLOAD_FILE_LOCATION_TYPE.LOCAL] ?? []),
            ].some((item) => !jobStatus.isComplete(item.status)),
        [uploadQueue],
    );

    const updateMyJobsTaskData = ({ jobId, taskIndex, data }) => {
        dispatch(updateMyJobsTask({ resource, jobId, taskIndex, data }));
    };

    const updateMyJobsData = ({ jobId, data }) => {
        dispatch(updateMyJobsJob({ resource, jobId, data }));
    };

    const deleteMyJobsData = (jobId) => {
        dispatch(deleteMyJobsJob({ resource, jobId }));
    };

    useEffect(() => {
        if (!isSynchronization) return;
        if (!store) return;
        const myJobsData = myJobs[resource]?.data;
        if (runningJobIds.length === 0) return;

        runningJobIds.map((jobId) => {
            let newDataFromMyJobs = myJobsData?.find((item) => item.jobId === jobId);
            if (!newDataFromMyJobs) return;

            let tasksFromFileData = fileData[resource]?.filter((item) => item.data.job.jobId === jobId);
            const jobTasks = tasksFromFileData.map(({ status, data, files }) => ({
                status,
                metadata: data?.metadata,
                name: data?.name,
                files,
            }));
            if (jobTasks) {
                newDataFromMyJobs.tasks = jobTasks;
                updateMyJobsData({ jobId, data: newDataFromMyJobs });

                if (newDataFromMyJobs.status === COMMON_JOB_STATUS.SUCCEED) {
                    const filteredJobIds = runningJobIds.filter((id) => id !== jobId);
                    dispatch(updateRunningJobIds({ resource, runningJobIds: filteredJobIds }));
                }
            }
        });
    }, [fileData?.[resource], isSynchronization]);

    const synchronize = () => {
        setIsSynchronization(true);
    };

    const createJobData = (customData) => {
        const jobId = nanoid();
        const data = {
            jobId: jobId, //Note: API 순서가 밀려있어서 retryKey를 넣을 수 있지는 않아보임.
            status: COMMON_JOB_STATUS.SUBMITTED,
            startAt: new Date().toISOString(),
            ...customData,
        };
        // setRunningJobIds([...runningJobIds, jobId]);
        updateMyJobsData({ jobId, data });
        return data;
    };

    useEffect(() => {
        if (!myJobs[resource]?.runningJobIds) return;
        setRunningJobIds(myJobs[resource]?.runningJobIds);
    }, [myJobs[resource]?.runningJobIds]);

    return {
        openMyJobs,
        changeMyJobsActiveTab,
        updateMyJobsData,
        updateMyJobsTaskData,
        deleteMyJobsData,
        createJobData,
        synchronize,
        isJobExisting,
        isJobProcessing,
        isBrowserUploading,
    };
};

export default useMyJobs;
